// src/components/laboratory/forms/LaboratoryForm.jsx
import React, { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import { CREATE_LABORATORY, UPDATE_LABORATORY } from '../../../api/laboratory-api';
import ModalComponent from '../../common/ModalComponent';
import { toast } from 'react-toastify';

const LaboratoryForm = ({ laboratory, users, isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    nom: '',
    userId: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize form data if editing an existing laboratory
  useEffect(() => {
    if (laboratory) {
      setFormData({
        nom: laboratory.nom || '',
        userId: laboratory.userId || '',
      });
    } else {
      // Reset form for a new laboratory
      setFormData({
        nom: '',
        userId: '',
      });
    }
    setFormErrors({});
  }, [laboratory, isOpen]);

  // Mutations
  const [createLaboratory] = useMutation(CREATE_LABORATORY, {
    onCompleted: (data) => {
      if (data.createLaboratory) {
        toast.success('Laboratoire créé avec succès');
        onSuccess();
      } else {
        toast.error('Erreur lors de la création du laboratoire');
        setIsSubmitting(false);
      }
    },
    onError: (error) => {
      toast.error(`Erreur lors de la création: ${error.message}`);
      setIsSubmitting(false);
    }
  });

  const [updateLaboratory] = useMutation(UPDATE_LABORATORY, {
    onCompleted: () => {
      toast.success('Laboratoire mis à jour avec succès');
      onSuccess();
    },
    onError: (error) => {
      toast.error(`Erreur lors de la mise à jour: ${error.message}`);
      setIsSubmitting(false);
    }
  });

  // Form input handler
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear error when field is filled
    if (value && formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  // Form validation
  const validateForm = () => {
    const errors = {};

    if (!formData.nom) errors.nom = 'Le nom du laboratoire est requis';
    if (!formData.userId) errors.userId = 'Un utilisateur propriétaire est requis';

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Form submission handler
  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      if (laboratory) {
        // Update existing laboratory
        await updateLaboratory({
          variables: {
            updateLaboratoryInput: {
              id: laboratory.id,
              nom: formData.nom,
              userId: formData.userId
            }
          }
        });
      } else {
        // Create new laboratory
        await createLaboratory({
          variables: {
            createLaboratoryInput: {
              nom: formData.nom,
              userId: formData.userId
            }
          }
        });
      }
      // Success is handled in the mutation callbacks
    } catch (error) {
      console.error('Error saving laboratory:', error);
      setIsSubmitting(false);
    }
  };

  return (
    <ModalComponent
      isOpen={isOpen}
      onClose={onClose}
      title={laboratory ? 'Modifier le laboratoire' : 'Créer un nouveau laboratoire'}
      onSubmit={handleSubmit}
      isSubmitting={isSubmitting}
    >
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nom du laboratoire <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="nom"
            value={formData.nom}
            onChange={handleInputChange}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${
              formErrors.nom ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="Laboratoire Pharma Plus"
          />
          {formErrors.nom && (
            <p className="mt-1 text-sm text-red-600">{formErrors.nom}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Utilisateur propriétaire <span className="text-red-500">*</span>
          </label>
          <select
            name="userId"
            value={formData.userId}
            onChange={handleInputChange}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${
              formErrors.userId ? 'border-red-300' : 'border-gray-300'
            }`}
          >
            <option value="">Sélectionner un utilisateur</option>
            {users.map(user => (
              <option key={user.id} value={user.id}>
                {user.email} {user.firstName && user.lastName ? `(${user.firstName} ${user.lastName})` : ''}
              </option>
            ))}
          </select>
          {formErrors.userId && (
            <p className="mt-1 text-sm text-red-600">{formErrors.userId}</p>
          )}
        </div>
      </div>
    </ModalComponent>
  );
};

export default LaboratoryForm;